import {
  Component,
  inject,
  signal,
  WritableSignal,
  OnInit,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import { MotifModule, MotifModalRef, ButtonSizes } from '@ey-xd/ng-motif';
import { IconoirTimer, IconoirXmark } from '@ey-xd/motif-icon';
import { EventService, IdleService } from '@services';
import {
  APP_EVENT_KEY,
  APP_TIMEOUT_SETTING_KEY,
  APP_WARNING_TIMEOUT_SETTING_KEY,
  DATE_TIME_FORMAT,
  DEFAULT_APP_TIMEOUT,
  DEFAULT_APP_WARNING_TIMEOUT,
} from '@models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { DatePipe } from '@angular/common';
import { map, merge, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingsService } from '@services/webapi/settings.service';

@Component({
  selector: 'app-logout-warning-modal',
  standalone: true,
  imports: [MotifModule, TranslateModule, CommonModule],
  providers: [DatePipe],
  templateUrl: './logout-warning-modal.component.html',
  styleUrl: './logout-warning-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogoutWarningModalComponent implements OnInit {
  #eventService: EventService = inject(EventService);
  #idleService: IdleService = inject(IdleService);
  #datePipe: DatePipe = inject(DatePipe);
  #translateService = inject(TranslateService);
  #dialogRef = inject(MotifModalRef<LogoutWarningModalComponent, boolean>);
  #destroyRef = inject(DestroyRef);
  #settingsService = inject(SettingsService);

  #appTimeout: number | null = null;
  #appWarningTimeout: number | null = null;

  #settings$ = this.#settingsService.getAll().pipe(
    map(settings => ({
      idleTimeout: settings.getNumberOrDefault(
        APP_TIMEOUT_SETTING_KEY,
        DEFAULT_APP_TIMEOUT
      ),
      warningTimeout: settings.getNumberOrDefault(
        APP_WARNING_TIMEOUT_SETTING_KEY,
        DEFAULT_APP_WARNING_TIMEOUT
      ),
    })),
    tap(settings => {
      this.#appTimeout = settings.idleTimeout;
      this.#appWarningTimeout = settings.warningTimeout;
    }),
    map(settings => settings.warningTimeout),
    takeUntilDestroyed(this.#destroyRef)
  );

  public readonly closeIcon: typeof IconoirXmark = IconoirXmark;
  public readonly timerIcon = IconoirTimer;
  public FORMAT: typeof DATE_TIME_FORMAT = DATE_TIME_FORMAT;
  public readonly SMALL_BUTTON_SIZE = ButtonSizes.SMALL;

  public readonly timeoutMessage: WritableSignal<string> = signal('');

  public ngOnInit() {
    merge(this.#settings$, this.#idleService.getTimeLeft())
      .pipe(
        map(timeLeft => {
          const timeLeftDate = new Date(timeLeft * 1000);

          const timeLeftMinutes = this.#datePipe.transform(
            timeLeftDate,
            "m'm'"
          );
          const timeLeftSeconds = this.#datePipe.transform(
            timeLeftDate,
            "ss's'"
          );

          if (timeLeft >= 60) {
            return `<strong>${timeLeftMinutes}</strong> and <strong>${timeLeftSeconds}</strong>`;
          } else {
            return `<strong>${timeLeftSeconds}</strong>`;
          }
        }),
        switchMap(formattedTimeLeft => {
          return this.#translateService.get('common.timeout.modal.message', {
            timeLeft: formattedTimeLeft,
          });
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(message => {
        this.timeoutMessage.set(message);
      });
  }

  public onLogout(): void {
    this.#dialogRef.close(false);
    this.#eventService.emitEvent(APP_EVENT_KEY.LOGOUT);
  }

  public onStay(): void {
    this.#dialogRef.close(true);
    this.#idleService.resetIdleTimer(
      this.#appTimeout!,
      this.#appWarningTimeout!
    );
  }
}
